// File: client/src/platform/modifiers.js
//
// Shared "global modifier" catalog — extracted now that ≥2 variants use it
// (Randomizer picks one every few turns; Mayhem picks one every turn; Party votes
// on them; Chaos toggles them). A modifier is a pure, seedable, engine-free board
// transformation or a rule flag the runtime interprets.
//
// A modifier:
//   id, name, icon, describe
//   kind: 'board' | 'rule'
//   apply(board, rng, ctx) -> string   ('board' modifiers mutate the board and
//                                        return a message; 'rule' modifiers mutate
//                                        nothing and return a message — the runtime
//                                        reads `rule` to change behaviour that turn)
//   rule?: string                       for kind:'rule' (e.g. 'knight_only')
//
// Kings are never destroyed by any modifier (they may be relocated).

const chebyshev = (a, b) => Math.max(Math.abs(a.row - b.row), Math.abs(a.col - b.col));
function allPieces(board, predicate) {
  const out = [];
  for (let r = 0; r < 8; r++) for (let c = 0; c < 8; c++) {
    const p = board[r][c];
    if (p && p !== '' && (!predicate || predicate(p, r, c))) out.push({ piece: p, row: r, col: c });
  }
  return out;
}
function emptySquares(board) {
  const out = [];
  for (let r = 0; r < 8; r++) for (let c = 0; c < 8; c++) if (!board[r][c]) out.push({ row: r, col: c });
  return out;
}
const isKing = (p) => p === 'K' || p === 'k';
const pick = (rng, arr) => (arr.length ? arr[Math.floor(rng() * arr.length)] : null);

// ---- board modifiers ----
export const boardRotation = {
  id: 'board_rotation', name: 'Board Rotation', icon: '🔄', kind: 'board',
  describe: 'The board rotates 180°.',
  apply(board) {
    const snap = board.map(row => [...row]);
    for (let r = 0; r < 8; r++) for (let c = 0; c < 8; c++) board[r][c] = snap[7 - r][7 - c];
    return 'The board rotated 180°.';
  },
};

export const randomTeleport = {
  id: 'random_teleport', name: 'Teleport', icon: '✨', kind: 'board',
  describe: 'A random piece teleports to an empty square.',
  apply(board, rng) {
    const movable = allPieces(board, p => !isKing(p));
    const dest = pick(rng, emptySquares(board));
    const piece = pick(rng, movable);
    if (!piece || !dest) return 'Teleport fizzled.';
    board[dest.row][dest.col] = piece.piece;
    board[piece.row][piece.col] = '';
    return `A ${piece.piece} teleported.`;
  },
};

export const randomPromotion = {
  id: 'random_promotion', name: 'Random Promotion', icon: '🎲', kind: 'board',
  describe: 'A random pawn is promoted to a random piece.',
  apply(board, rng) {
    const pawns = allPieces(board, p => p === 'P' || p === 'p');
    const t = pick(rng, pawns);
    if (!t) return 'No pawns to promote.';
    const isWhite = t.piece === 'P';
    const to = pick(rng, isWhite ? ['Q', 'R', 'B', 'N'] : ['q', 'r', 'b', 'n']);
    board[t.row][t.col] = to;
    return `A pawn was promoted to ${to}.`;
  },
};

export const pieceSwap = {
  id: 'piece_swap', name: 'Piece Swap', icon: '🔀', kind: 'board',
  describe: 'Two random pieces swap places.',
  apply(board, rng) {
    const pieces = allPieces(board, p => !isKing(p));
    if (pieces.length < 2) return 'Not enough pieces to swap.';
    const a = pick(rng, pieces);
    let b = pick(rng, pieces);
    let guard = 0;
    while (b && a && b.row === a.row && b.col === a.col && guard++ < 10) b = pick(rng, pieces);
    if (!a || !b) return 'Swap fizzled.';
    board[a.row][a.col] = b.piece;
    board[b.row][b.col] = a.piece;
    return `A ${a.piece} and a ${b.piece} swapped.`;
  },
};

export const gravity = {
  id: 'gravity', name: 'Gravity', icon: '⬇️', kind: 'board',
  describe: 'Every piece falls to the bottom of its column.',
  apply(board) {
    for (let c = 0; c < 8; c++) {
      const col = [];
      for (let r = 0; r < 8; r++) if (board[r][c]) col.push(board[r][c]);
      for (let r = 0; r < 8; r++) board[r][c] = '';
      let r = 7;
      for (let i = col.length - 1; i >= 0; i--) board[r--][c] = col[i];
    }
    return 'Gravity pulled every piece down.';
  },
};

export const meteor = {
  id: 'meteor', name: 'Meteor Strike', icon: '☄️', kind: 'board',
  describe: 'A random non-king piece is destroyed.',
  apply(board, rng) {
    const t = pick(rng, allPieces(board, p => !isKing(p)));
    if (!t) return 'The meteor missed.';
    board[t.row][t.col] = '';
    return `A meteor destroyed a ${t.piece}.`;
  },
};

// ---- rule modifiers (no board mutation; runtime reads `rule`) ----
export const knightOnly = {
  id: 'knight_only', name: 'Knight Fever', icon: '🐴', kind: 'rule', rule: 'knight_only',
  describe: 'Every piece moves like a knight this turn.',
  apply() { return 'Every piece moves like a knight this turn.'; },
};

export const frozen = {
  id: 'frozen', name: 'Frozen Pieces', icon: '🧊', kind: 'rule', rule: 'frozen_random',
  describe: 'A random piece is frozen and cannot move this turn.',
  apply() { return 'A random piece is frozen this turn.'; },
};

export const diagonalPawns = {
  id: 'diagonal_pawns', name: 'Diagonal Pawns', icon: '↗️', kind: 'rule', rule: 'diagonal_pawns',
  describe: 'Pawns move diagonally forward (and capture straight) this turn.',
  apply() { return 'Pawns move diagonally this turn.'; },
};

// The full shared catalog. Variants may use all or a subset.
export const MODIFIERS = [
  boardRotation, randomTeleport, randomPromotion, pieceSwap, gravity, meteor,
  knightOnly, frozen, diagonalPawns,
];

export const BOARD_MODIFIERS = MODIFIERS.filter(m => m.kind === 'board');

/** Pick a modifier from a pool using the rng (pure). */
export function pickModifier(rng, pool = MODIFIERS) {
  return pick(rng, pool);
}

/** Look up a modifier by id in a pool. */
export function modifierById(id, pool = MODIFIERS) {
  return pool.find(m => m.id === id) || null;
}

/** Pick N distinct modifiers (for Party's vote). */
export function pickDistinct(rng, n, pool = MODIFIERS) {
  const copy = [...pool];
  const out = [];
  for (let i = 0; i < n && copy.length; i++) {
    const idx = Math.floor(rng() * copy.length);
    out.push(copy.splice(idx, 1)[0]);
  }
  return out;
}
